import type { InspectionResponseSchema } from "@/hera-api/client/types.gen";

/**
 * Resolve the pending inspection for a license from license details.
 *
 * Prefer matching a portal `pending_inspection_id` hint when present; otherwise
 * the first INSPECTION_PENDING row on the license. Used by the field portal
 * because GET /shared/inspections is blocked for registrant users.
 */
export function findPendingInspectionOnLicense(
  inspections: InspectionResponseSchema[] | null | undefined,
  pendingIdHint?: string | null,
): InspectionResponseSchema | null {
  const list = inspections ?? [];
  if (pendingIdHint) {
    const byId =
      list.find(
        (insp) =>
          insp.id === pendingIdHint && insp.status === "INSPECTION_PENDING",
      ) ?? null;
    if (byId) return byId;
  }
  return list.find((insp) => insp.status === "INSPECTION_PENDING") ?? null;
}

/** Latest submitted or finalized inspection on a license (committed / compliant / non-compliant). */
export function findCompletedInspectionOnLicense(
  inspections: InspectionResponseSchema[] | null | undefined,
  completedIdHint?: string | null,
): InspectionResponseSchema | null {
  const list = inspections ?? [];
  const isCompleted = (status: InspectionResponseSchema["status"]) =>
    status === "COMMITTED" ||
    status === "COMPLIANT" ||
    status === "NON_COMPLIANT";
  if (completedIdHint) {
    const byId =
      list.find(
        (insp) => insp.id === completedIdHint && isCompleted(insp.status),
      ) ?? null;
    if (byId) return byId;
  }
  return list.find((insp) => isCompleted(insp.status)) ?? null;
}
